import { useState, useEffect } from 'react'
import { FaPlus, FaTrash, FaCheckCircle, FaTimesCircle } from 'react-icons/fa'
import { useProviderProfile } from '@/hooks/useProfile'
import { useConfigureProfile } from '@/hooks/useProviderConfiguration'
import Button from '@/components/common/Button/Button'
import Spinner from '@/components/common/Spinner/Spinner'
import './Steps.css'

interface TimeSlot {
    start: string
    end: string
}

interface DaySchedule {
    enabled: boolean
    slots: TimeSlot[]
}

interface AvailabilityStepProps {
    onComplete?: () => void
}

const AvailabilityStep = ({ onComplete }: AvailabilityStepProps) => {
    const { data: providerData, isLoading: providerLoading } = useProviderProfile()
    const configureProfile = useConfigureProfile()
    
    const [schedule, setSchedule] = useState<Record<string, DaySchedule>>({
        monday: { enabled: false, slots: [] },
        tuesday: { enabled: false, slots: [] },
        wednesday: { enabled: false, slots: [] },
        thursday: { enabled: false, slots: [] },
        friday: { enabled: false, slots: [] },
        saturday: { enabled: false, slots: [] },
        sunday: { enabled: false, slots: [] },
    })
    const [saving, setSaving] = useState(false)
    const districtIds = Array.isArray(providerData?.service_districts)
        ? providerData.service_districts
              .map((district: any) => (typeof district === 'number' ? district : district?.id))
              .filter((id: number | undefined): id is number => typeof id === 'number')
        : []

    const days = [
        { key: 'monday', label: 'Lunes', short: 'L' },
        { key: 'tuesday', label: 'Martes', short: 'M' },
        { key: 'wednesday', label: 'Miércoles', short: 'X' },
        { key: 'thursday', label: 'Jueves', short: 'J' },
        { key: 'friday', label: 'Viernes', short: 'V' },
        { key: 'saturday', label: 'Sábado', short: 'S' },
        { key: 'sunday', label: 'Domingo', short: 'D' },
    ]

    // Load existing schedule from provider data
    useEffect(() => {
        if (providerData?.availability_schedule) {
            console.log('📥 Fetched availability data:', providerData.availability_schedule)
            const formattedSchedule: Record<string, DaySchedule> = {
                monday: providerData.availability_schedule.monday || { enabled: false, slots: [] },
                tuesday: providerData.availability_schedule.tuesday || { enabled: false, slots: [] },
                wednesday: providerData.availability_schedule.wednesday || { enabled: false, slots: [] },
                thursday: providerData.availability_schedule.thursday || { enabled: false, slots: [] },
                friday: providerData.availability_schedule.friday || { enabled: false, slots: [] },
                saturday: providerData.availability_schedule.saturday || { enabled: false, slots: [] },
                sunday: providerData.availability_schedule.sunday || { enabled: false, slots: [] },
            }
            setSchedule(formattedSchedule)
        }
    }, [providerData])

    const toggleDay = (dayKey: string) => {
        setSchedule(prev => ({
            ...prev,
            [dayKey]: {
                ...prev[dayKey],
                enabled: !prev[dayKey].enabled,
            }
        }))
    }

    const addTimeSlot = (dayKey: string) => {
        setSchedule(prev => ({
            ...prev,
            [dayKey]: {
                ...prev[dayKey],
                slots: [...prev[dayKey].slots, { start: '09:00', end: '17:00' }]
            }
        }))
    }

    const removeTimeSlot = (dayKey: string, slotIndex: number) => {
        setSchedule(prev => ({
            ...prev,
            [dayKey]: {
                ...prev[dayKey],
                slots: prev[dayKey].slots.filter((_, index) => index !== slotIndex)
            }
        }))
    }

    const updateTimeSlot = (dayKey: string, slotIndex: number, field: 'start' | 'end', value: string) => {
        setSchedule(prev => ({
            ...prev,
            [dayKey]: {
                ...prev[dayKey],
                slots: prev[dayKey].slots.map((slot, index) => 
                    index === slotIndex ? { ...slot, [field]: value } : slot
                )
            }
        }))
    }

    const handleSave = async () => {
        const enabledDays = Object.values(schedule).filter(day => day.enabled)
        if (enabledDays.length === 0) {
            alert('Debes seleccionar al menos un día disponible')
            return
        }

        if (districtIds.length === 0) {
            alert('Primero configura tu cobertura seleccionando al menos un distrito.')
            return
        }

        setSaving(true)
        try {
            console.log('📤 Saving availability schedule:', schedule)

            await configureProfile.mutateAsync({
                availability_schedule: schedule,
                district_ids: districtIds,
            })

            console.log('✅ Availability schedule saved successfully')
            alert('Disponibilidad guardada correctamente')
            onComplete?.()
        } catch (error) {
            console.error('❌ Error saving availability:', error)
            alert(`Error al guardar el horario: ${error instanceof Error ? error.message : 'Error desconocido'}`)
        } finally {
            setSaving(false)
        }
    }

    if (providerLoading) {
        return (
            <div className="step-container">
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '3rem 0' }}>
                    <Spinner />
                    <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>Cargando horarios...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="step-container">
            <div className="step-section">
                <h4 className="step-section-title">Tu Disponibilidad</h4>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                    Selecciona los días y horarios en los que puedes trabajar
                </p>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {days.map(day => {
                        const daySchedule = schedule[day.key]
                        const hasTimeSlots = daySchedule.slots && daySchedule.slots.length > 0
                        
                        return (
                            <div key={day.key} className="step-availability-day">
                                <div 
                                    className={`step-availability-day-header ${daySchedule.enabled ? 'step-availability-day-header--enabled' : ''}`}
                                    onClick={() => toggleDay(day.key)}
                                >
                                    <div className="step-availability-day-toggle">
                                        <div className={`step-availability-day-circle ${daySchedule.enabled ? 'step-availability-day-circle--enabled' : ''}`}>
                                            {day.short}
                                        </div>
                                        <span className="step-availability-day-label">{day.label}</span>
                                    </div>
                                    
                                    {!daySchedule.enabled && (
                                        <div className="step-availability-status">
                                            {hasTimeSlots ? (
                                                <>
                                                    <FaCheckCircle style={{ color: 'var(--success-500)', fontSize: '0.875rem' }} />
                                                    <span style={{ fontSize: '0.75rem', color: 'var(--success-500)' }}>Disponible</span>
                                                </>
                                            ) : (
                                                <>
                                                    <FaTimesCircle style={{ color: 'var(--text-tertiary)', fontSize: '0.875rem' }} />
                                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>No disponible</span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {daySchedule.enabled && (
                                    <div className="step-availability-day-content">
                                        {daySchedule.slots.length === 0 ? (
                                            <div className="step-availability-no-slots">
                                                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                                                    No hay horarios configurados para este día
                                                </p>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => addTimeSlot(day.key)}
                                                >
                                                    <FaPlus /> Agregar primer horario
                                                </Button>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="step-availability-slots">
                                                    {daySchedule.slots.map((slot, index) => (
                                                        <div key={index} className="step-availability-slot">
                                                            <div className="step-availability-slot-times">
                                                                <input
                                                                    type="time"
                                                                    value={slot.start}
                                                                    onChange={(e) => updateTimeSlot(day.key, index, 'start', e.target.value)}
                                                                    className="step-time-input"
                                                                />
                                                                <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>hasta</span>
                                                                <input
                                                                    type="time"
                                                                    value={slot.end}
                                                                    onChange={(e) => updateTimeSlot(day.key, index, 'end', e.target.value)}
                                                                    className="step-time-input"
                                                                />
                                                            </div>
                                                            <button
                                                                onClick={() => removeTimeSlot(day.key, index)}
                                                                className="step-availability-slot-remove"
                                                                title="Eliminar horario"
                                                            >
                                                                <FaTrash />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                                
                                                {daySchedule.slots.length < 3 && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => addTimeSlot(day.key)}
                                                        style={{ marginTop: '0.75rem' }}
                                                    >
                                                        <FaPlus /> Agregar horario
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>

            {/* <div className="step-tips">
                <h5 className="step-tips-title">💡 Consejos</h5>
                <div className="step-tip">• Puedes tener hasta 3 horarios diferentes por día</div>
                <div className="step-tip">• Toca las horas para cambiarlas</div>
                <div className="step-tip">• Los clientes verán estos horarios al reservar</div>
            </div> */}

            <div className="step-actions">
                <Button
                    variant="primary"
                    size="lg"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? 'Guardando...' : 'Guardar Disponibilidad'}
                </Button>
            </div>
        </div>
    )
}

export default AvailabilityStep
